import React, { useRef } from 'react';
import { Alert, Box, Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const SourcePayloadUploader = ({
  inputMode,
  onInputModeChange,
  value,
  onChange,
  onApplyJson,
  onApplyXlsx,
  xlsxFileName,
  parseError,
  samplePayload,
  disabled,
}) => {
  const jsonFileInputRef = useRef(null);
  const xlsxFileInputRef = useRef(null);
  const isJsonMode = inputMode === 'json';
  const hasParsedSample = Boolean(samplePayload) && !parseError;

  const handleJsonFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      onChange(text);
      onApplyJson(text);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleXlsxFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    await onApplyXlsx(file);
    event.target.value = '';
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Sample ERP payload
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload or paste a representative invoice (or credit note) sample from your ERP. JSON works for nested API
        payloads; XLSX matches the flat spreadsheet shape used by SFTP ingestion (one document per file). The sample is
        sent to <strong>Run preview</strong> only — it is not stored on the server.
      </Typography>

      <ToggleButtonGroup
        exclusive
        value={inputMode}
        onChange={(_, nextMode) => {
          if (nextMode) {
            onInputModeChange(nextMode);
          }
        }}
        size="small"
        sx={{ mb: 2 }}
        disabled={disabled}
      >
        <ToggleButton value="json">JSON</ToggleButton>
        <ToggleButton value="xlsx">XLSX</ToggleButton>
      </ToggleButtonGroup>

      {isJsonMode ? (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <input ref={jsonFileInputRef} type="file" accept=".json,application/json" hidden onChange={handleJsonFile} />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => jsonFileInputRef.current?.click()}
            disabled={disabled}
          >
            Upload JSON file
          </Button>
          <Button variant="contained" onClick={() => onApplyJson(value)} disabled={disabled}>
            Parse sample
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <input
            ref={xlsxFileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            hidden
            onChange={handleXlsxFile}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => xlsxFileInputRef.current?.click()}
            disabled={disabled}
          >
            Upload XLSX file
          </Button>
          {xlsxFileName && (
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {xlsxFileName}
            </Typography>
          )}
        </Stack>
      )}

      <TextField
        fullWidth
        multiline
        minRows={12}
        maxRows={24}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || !isJsonMode}
        placeholder={
          isJsonMode
            ? '{ "InvoiceNumber": "INV-001", ... }'
            : 'Upload an XLSX file to generate the preview payload JSON.'
        }
        sx={{
          '& .MuiInputBase-root': {
            fontFamily: 'monospace',
            fontSize: '0.8rem',
          },
        }}
      />

      {!isJsonMode && hasParsedSample && (
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => onApplyJson(value)} disabled={disabled}>
          Re-parse edited JSON
        </Button>
      )}

      {parseError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {parseError}
        </Alert>
      )}

      {hasParsedSample && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Sample parsed successfully — ready for preview.
        </Alert>
      )}
    </Box>
  );
};

export default SourcePayloadUploader;
